import {Fragment, useEffect, useMemo, useRef, useState} from 'react';
// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Dialog, Transition} from '@headlessui/react';
import clsx from 'clsx';
import {
  CloseMenuIcon,
  PostalSearchForm,
  ProductPrices,
  useAppContext,
  RoundedModal,
  SparkleIcon,
} from '~/components';
import {
  Product as ProductType,
  SellingPlan,
} from '@shopify/hydrogen/storefront-api-types';
import {ArrowLeftIcon} from '../icons/ArrowLeftIcon';
import {useQuery} from 'react-query';
import {Image, useProductOptions} from '@shopify/hydrogen';
import {
  IShopifyProductVariantTimeslotsInput,
  IVariantTimeslot,
  ShopifyProductVariantTimeslotsDocument,
} from '~/lib/types/graphql/api';
import {DISPLAY_TIMEZONE, queryBe, scaleWidth} from '~/utils';
import {
  ProductSelectService,
  getValueProperty,
} from './ProductSelectService.client';
import {ProductSelectTimeslot} from './ProductSelectTimeslot.client';
import {ProductSelectPlan} from './ProductSelectPlan.client';
import {localTimezone} from '~/utils/tz';
import {formatInTimeZone} from 'date-fns-tz';
import {ProductPlanType} from '~/constants/ProductPlanType';
import {ProductSavingPlanType} from '~/constants/ProductSavingPlanType';
import {v4} from 'uuid';
import {ProductBookingModalConfirmation} from '../booking_modal/ProductBookingModal.client';
import {useMobile} from '~/hooks';
import {ProductDuration} from '../products/ProductDuration';

interface Props {
  isOpen: boolean;
  isGift: boolean;
  onClose: () => void;
  product: ProductType;
  planType: ProductPlanType;
  setIsGift: (isGift: boolean) => void;
  setPlanType: (planType: ProductPlanType) => void;
  onConfirmTimeslot: (confirmation: ProductBookingModalConfirmation) => void;
}

const STEP_SERVICE = 0;
const STEP_TIMESLOT = 1;
const STEP_PLAN = 2;

function calculateSelectSellingGroup(
  _planType: ProductPlanType,
  _savingPlan: ProductSavingPlanType,
  _sellingPlanGroups: ReturnType<typeof useProductOptions>['sellingPlanGroups'],
  _frequency: string | undefined,
) {
  const gbbSellingPlan = _sellingPlanGroups?.find(
    (item) => item?.name === 'GBB' || item?.name === 'GBB 10%',
  );
  const descriptionMatcher = `${_savingPlan}-${
    _planType === 'one-time' ? 'single' : 'recurrence'
  }${_frequency ? `-${_frequency}` : ''}`;

  const found = gbbSellingPlan?.sellingPlans?.find(
    (item) => item?.description?.toLowerCase() === descriptionMatcher,
  );

  return found;
}

export function ProductBookingSteps({
  isOpen,
  isGift,
  product,
  onClose,
  planType,
  setPlanType,
  setIsGift,
  onConfirmTimeslot,
}: Props) {
  const {searchPostcode} = useAppContext();
  const [date, setDate] = useState<Date>(new Date());
  const {
    selectedVariant,
    selectedOptions,
    selectedSellingPlan,
    sellingPlanGroups,
    setSelectedSellingPlan,
  } = useProductOptions();
  const [currentStep, setCurrentStep] = useState<number>(STEP_SERVICE);
  const [selectedTimeslot, setSelectedTimeslot] = useState<
    IVariantTimeslot | undefined
  >();
  const [savingPlan, setSavingPlan] = useState<ProductSavingPlanType>(
    ProductSavingPlanType.Standard,
  );

  const [savingPlanPrice, setSavingPlanPrice] = useState<number>(0);
  const [frequency, setFrequency] = useState<string>();

  const [closeConfirmModal, setCloseConfirmModal] = useState<boolean>(false);
  const isMobile = useMobile();
  const bookingStepRef = useRef<HTMLDivElement>(null);

  const productTitleArr = (product?.title ?? 'Product Title').split(' ');
  const productTime = product?.productType?.split('|')[0]?.trim() ?? '';

  const selectedVariantPriceObject =
    selectedVariant?.price ?? selectedVariant?.priceV2;
  const selectedVariantPrice = Number(selectedVariantPriceObject?.amount);

  const hasGbbPlan = useMemo(() => {
    return sellingPlanGroups?.find(
      (item) => item?.name === 'GBB' || item?.name === 'GBB 10%',
    );
  }, [sellingPlanGroups]);

  const getNextStepBtnTitle = () => {
    if (currentStep == STEP_SERVICE) return 'Add Service';
    if (currentStep == STEP_TIMESLOT && hasGbbPlan) return 'Select Timeslot';
    // if (currentStep == STEP_PLAN)
    return (
      <div className="flex items-center gap-2">
        <div>Add to Cart</div>
        <SparkleIcon className="sparkle-icon w-[12px]" fill="#FFFFFF" />
        <div>
          <ProductPrices
            forBookNow
            // displayFromPrice={false}
            selectedTimeslot={selectedTimeslot}
            includeSurgeFee
          />
        </div>
      </div>
    );
  };

  let sellingPlanPrice: number;
  if (planType === 'one-time') {
    let discountOnetime = 0; // Properly typed as number and initialized to 0

    if (savingPlan === 'saver') {
      discountOnetime = 10;
    } else if (savingPlan === 'standard') {
      discountOnetime = 5;
    }

    // const surgeFee = Number(selectedTimeslot?.surgeFee?.amount ?? 0);
    const discount = discountOnetime / 100; // Explicitly convert discountOnetime to a fraction
    sellingPlanPrice = selectedVariantPrice - selectedVariantPrice * discount;

    // console.log('One time sellingPlanPrice>>>>>>', sellingPlanPrice);
  } else {
    if (selectedSellingPlan) {
      const adjustmentValue =
        selectedSellingPlan?.priceAdjustments?.[0]?.adjustmentValue;

      // Selling Price = price - fixed amount
      if (
        adjustmentValue &&
        'adjustmentAmount' in adjustmentValue &&
        adjustmentValue.adjustmentAmount
      ) {
        sellingPlanPrice =
          selectedVariantPrice -
          Number(adjustmentValue.adjustmentAmount.amount);
      }

      // Selling Price = fixed price
      if (
        adjustmentValue &&
        'price' in adjustmentValue &&
        adjustmentValue.price
      ) {
        sellingPlanPrice = Number(adjustmentValue.price.amount);
      }

      // Selling Price = price - (price * percentage)
      if (
        adjustmentValue &&
        'adjustmentPercentage' in adjustmentValue &&
        adjustmentValue.adjustmentPercentage
      ) {
        sellingPlanPrice =
          selectedVariantPrice -
          selectedVariantPrice *
            Number(adjustmentValue.adjustmentPercentage / 100);
      }

      if (savingPlanPrice) {
        const discount = Number(savingPlanPrice) / 100; // Explicitly convert savingPlanPrice to number
        sellingPlanPrice =
          selectedVariantPrice - selectedVariantPrice * discount;
      }
    }
  }

  const {
    data: timeslots,
    isLoading: isLoadingTimeslots,
    error: timeslotsError,
    // Linter doesn't understand that this is react-query useQuery, not server side one
    // eslint-disable-next-line hydrogen/client-component-banned-hooks
  } = useQuery(
    ['product-variant-timeslots', selectedVariant?.id, date, searchPostcode],
    async () => {
      const {data, errors} = await queryBe<
        'shopifyProductVariantTimeslots',
        (input: IShopifyProductVariantTimeslotsInput) => IVariantTimeslot[]
      >({
        query: ShopifyProductVariantTimeslotsDocument,
        variables: {
          input: {
            id: selectedVariant?.id ?? '',
            date: {
              year: date.getFullYear(),
              month: date.getMonth(),
              day: date.getDate(),
            },
            postcode: searchPostcode,
          },
        },
      });
      if (errors) {
        throw new Error(errors.map((e) => e.message).join(', '));
      }

      return data.shopifyProductVariantTimeslots;
    },
    {
      retry: false,
      enabled: searchPostcode.length === 6,
    },
  );

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(STEP_SERVICE);
    }
  }, [isOpen]);

  const renderStickyProductInfo = () => (
    <div className="border-t-2 border-clay-100 bg-[#FAFAFA] px-10 py-3">
      <div className="flex justify-between">
        {/* Begin Product Title */}
        <div
          className="font-semibold leading-none text-[#4B2304]"
          style={{fontSize: scaleWidth(14)}}
        >
          {productTitleArr[0]}{' '}
          {productTitleArr[1] == 'Glow' && productTitleArr[1]}
          {productTitleArr[1] == 'a' && productTitleArr[1]}
          {productTitleArr.length > 1 && (
            <>
              {' '}
              <span className="font-serif italic">
                {productTitleArr
                  .filter(
                    (str, index) =>
                      index !== 0 && str !== 'Glow' && str !== 'a',
                  )
                  .join(' ')}
              </span>
            </>
          )}
        </div>
        <ProductDuration />
      </div>
      {/* Begin Service Info */}
      <div
        className="mt-[8px] leading-[119%] text-[#4B2304]"
        style={{fontSize: scaleWidth(14)}}
      >
        {selectedOptions
          ? Object.values(selectedOptions)
              .filter((item) => item !== '-')
              .map((item, index) => (
                <span key={item}>
                  {index > 0 && <span className="mx-1">/</span>}
                  <span>{getValueProperty(item ?? '').title}</span>
                </span>
              ))
          : null}
      </div>
      {/* End Service Info */}
      {/* Begin Timeslot Info */}
      {currentStep >= STEP_TIMESLOT && (
        <div
          className="mt-[8px] flex items-center leading-none tracking-tight text-acai-900"
          style={{fontSize: scaleWidth(14)}}
        >
          <Image
            src="/ic_calendar.png"
            alt="calendar-icon"
            width={16}
            height={16}
            className="h-[16px] w-[16px]"
          />
          <span className="ml-[4px]">
            {formatInTimeZone(date, localTimezone, 'EEE, d LLL yyyy')}
          </span>
          {currentStep == STEP_PLAN ? (
            selectedTimeslot && (
              <span className="ml-1">
                -{' '}
                {formatInTimeZone(
                  new Date(selectedTimeslot.dateTime),
                  DISPLAY_TIMEZONE,
                  'HH:mm',
                )}
              </span>
            )
          ) : (
            <div className="ml-[8px] rounded-full bg-acai-900 px-2 py-[6px] text-[11px] leading-none text-white">
              {selectedTimeslot
                ? formatInTimeZone(
                    new Date(selectedTimeslot.dateTime),
                    DISPLAY_TIMEZONE,
                    'HH:mm',
                  )
                : 'No selected timeslot'}
            </div>
          )}
        </div>
      )}
      <div
        className="mt-[8px] flex items-center gap-2 font-bold leading-none text-acai-900"
        style={{fontSize: scaleWidth(14)}}
      >
        <ProductPrices
          hasStartFrom={currentStep !== STEP_PLAN}
          selectedTimeslot={selectedTimeslot}
          includeSurgeFee={currentStep !== STEP_SERVICE}
        />
        {currentStep == STEP_PLAN && (
          <div className="ml-[8px] rounded-full bg-acai-900 p-[6px] text-[11px] capitalize leading-none text-white">
            {savingPlan} Plan
          </div>
        )}
      </div>
      <div style={{marginTop: scaleWidth(16)}}>
        <button
          className={
            'flex w-full items-center justify-center rounded-full border border-daybreak-400 bg-daybreak-400 text-center leading-none text-white duration-300 active:bg-daybreak-400 active:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300'
          }
          style={{
            fontSize: scaleWidth(14),
            paddingTop: scaleWidth(16),
            paddingBottom: scaleWidth(16),
          }}
          onClick={() => {
            if (currentStep == STEP_SERVICE) {
              setCurrentStep(currentStep + 1);
              bookingStepRef.current?.scrollTo({top: 0});
            } else if (currentStep == STEP_TIMESLOT && hasGbbPlan) {
              setCurrentStep(currentStep + 1);
              bookingStepRef.current?.scrollTo({top: 0});
            } else {
              //Should never happen, but just to be safe
              if (
                !selectedTimeslot ||
                !selectedVariant ||
                !selectedVariant.id
              ) {
                return;
              }
              //Generate an id for matching surge fee lines
              const tfrLineId = v4();
              const cart_line = {
                timeslot: selectedTimeslot,
                productLine: {
                  merchandiseId: selectedVariant.id,
                  sellingPlanId: selectedSellingPlan?.id,
                  attributes: [
                    {
                      key: 'Date & Time',
                      value: formatInTimeZone(
                        new Date(selectedTimeslot.dateTime),
                        DISPLAY_TIMEZONE,
                        'MMM d yyy, hh:mm b',
                      ),
                    },
                    {
                      key: '_tfr_line_id',
                      value: tfrLineId,
                    },
                    {
                      key: '_start_time',
                      value: new Date(selectedTimeslot.dateTime).toISOString(),
                    },
                    {
                      key: '_timezone',
                      value: 'Singapore',
                    },
                    {
                      key: '_postcode',
                      value: searchPostcode,
                    },
                    {
                      key: '_tfr_booking',
                      value: 'true',
                    },
                    {
                      key: '_tfr_is_gift',
                      value: isGift ? 'true' : 'false',
                    },
                    {
                      key: '_tfr_consultant_ids',
                      value: JSON.stringify(selectedTimeslot.consultantIds),
                    },
                    {
                      key: '_tfr_booking_creator_role',
                      value: 'user',
                    },
                    {
                      key: '_tfr_booking_price',
                      value: sellingPlanPrice
                        ? sellingPlanPrice.toString()
                        : selectedVariantPrice.toString(),
                    },
                  ],
                },
                //We only do surge fees for one-time bookings, ie no selling plan selected
                feeLine:
                  selectedTimeslot.surgeFee &&
                  !selectedSellingPlan?.recurringDeliveries
                    ? {
                        merchandiseId:
                          selectedTimeslot.surgeFee.variantShopifyId,
                        attributes: [
                          {key: '_for_tfr_line_id', value: tfrLineId},
                          {
                            key: '_tfr_booking_price',
                            value: sellingPlanPrice
                              ? sellingPlanPrice.toString()
                              : selectedVariantPrice.toString(),
                          },
                        ],
                      }
                    : undefined,
              };

              if (selectedSellingPlan && selectedSellingPlan.name) {
                cart_line.productLine.attributes.push({
                  key: '_tfr_booking_plan',
                  value: selectedSellingPlan.name,
                });
              }
              onConfirmTimeslot(cart_line);
              setTimeout(() => onClose(), 100);
            }
          }}
          disabled={
            searchPostcode.length === 0 ||
            (currentStep == STEP_TIMESLOT && !selectedTimeslot)
          }
        >
          {getNextStepBtnTitle()}
        </button>
      </div>
      {/* End Next Step Button */}
    </div>
  );

  useEffect(() => {
    if (currentStep === STEP_PLAN) {
      const found = calculateSelectSellingGroup(
        planType,
        savingPlan,
        sellingPlanGroups,
        frequency,
      );

      if (found) {
        setSelectedSellingPlan(found as SellingPlan);
      }
    }
  }, [
    frequency,
    planType,
    savingPlan,
    sellingPlanGroups,
    setSelectedSellingPlan,
    currentStep,
  ]);

  useEffect(() => {
    if (planType === 'one-time') {
      setFrequency(undefined);
    }
  }, [planType]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="h-full w-full text-center md:flex md:items-center md:justify-center md:p-4 md:pt-[80px]">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  'booking-steps-wrapper',
                  !isMobile && 'max-h-[800px] max-w-[732px] rounded-lg',
                )}
              >
                <StepProgressBar
                  step={currentStep}
                  timeslots={timeslots}
                  timeslotsError={timeslotsError}
                  isLoadingTimeslots={isLoadingTimeslots}
                  onPrevious={() => {
                    if (currentStep == STEP_SERVICE) {
                      setCloseConfirmModal(true);
                    } else {
                      setCurrentStep(currentStep - 1);
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      setSelectedSellingPlan(null);
                      bookingStepRef.current?.scrollTo({top: 0});
                    }
                  }}
                  onClose={() => {
                    setCloseConfirmModal(true);
                  }}
                  hasGbbPlan={!!hasGbbPlan}
                />
                <div
                  className="mb-4 flex-grow overflow-auto py-[8px]"
                  ref={bookingStepRef}
                  style={{
                    paddingLeft: scaleWidth(40),
                    paddingRight: scaleWidth(40),
                  }}
                >
                  {currentStep == STEP_SERVICE ? (
                    <ProductSelectService />
                  ) : currentStep == STEP_TIMESLOT ? (
                    <ProductSelectTimeslot
                      date={date}
                      productTitle={product.title}
                      onSetDate={(pDate: Date) => {
                        setDate(pDate);
                      }}
                      timeslots={timeslots}
                      timeslotsError={timeslotsError}
                      selectedVariant={selectedVariant}
                      selectedTimeslot={selectedTimeslot}
                      isLoadingTimeslots={isLoadingTimeslots}
                      selectedSellingPlan={selectedSellingPlan}
                      onSetSelectedTimeslot={(v) => {
                        setSelectedTimeslot(v);
                      }}
                    />
                  ) : currentStep == STEP_PLAN ? (
                    <ProductSelectPlan
                      isGift={isGift}
                      setIsGift={setIsGift}
                      planType={planType}
                      setPlanType={setPlanType}
                      savingPlan={savingPlan}
                      setSavingPlan={setSavingPlan}
                      setSavingPlanPrice={setSavingPlanPrice}
                      selectedTimeslot={selectedTimeslot}
                      frequency={frequency}
                      setFrequency={setFrequency}
                    />
                  ) : null}
                </div>
                {renderStickyProductInfo()}
                <RoundedModal
                  isOpen={closeConfirmModal}
                  onClose={() => {
                    setCloseConfirmModal(false);
                  }}
                  onConfirm={() => {
                    setCloseConfirmModal(false);
                    onClose();
                  }}
                  confirmButtonText="Exit"
                >
                  <div
                    className="py-8"
                    style={{
                      paddingLeft: scaleWidth(24),
                      paddingRight: scaleWidth(24),
                    }}
                  >
                    <div
                      className="text-[22px] italic text-daybreak-400"
                      style={{fontFamily: 'GT-Alpina'}}
                    >
                      Oops!
                    </div>
                    <div
                      className="mt-4 tracking-tight"
                      style={{
                        fontSize: scaleWidth(15),
                        letterSpacing: -scaleWidth(0.3),
                      }}
                    >
                      Your Refresh isn&apos;t ready to go just yet.{'\n'} Click
                      &apos;Back&apos; to continue your booking.
                    </div>
                  </div>
                </RoundedModal>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

interface StepProgressBarProps {
  step: number;
  timeslotsError: any;
  onClose: () => void;
  onPrevious: () => void;
  isLoadingTimeslots: boolean;
  timeslots: IVariantTimeslot[] | undefined;
  hasGbbPlan: boolean;
}

function StepProgressBar({
  step,
  onClose,
  timeslots,
  onPrevious,
  timeslotsError,
  isLoadingTimeslots,
  hasGbbPlan,
}: StepProgressBarProps) {
  const steps = hasGbbPlan
    ? [STEP_SERVICE, STEP_TIMESLOT, STEP_PLAN]
    : [STEP_SERVICE, STEP_TIMESLOT];
  const stepTitles = ['Service', 'Timeslot', 'Plan'];

  return (
    <div>
      <div
        className={clsx(
          'grid w-full gap-2',
          hasGbbPlan ? 'grid-cols-3' : 'grid-cols-2',
        )}
      >
        {steps.map((item) => (
          <div key={'step' + item} className="w-full bg-clay-100">
            <div
              className={clsx(
                'h-2 bg-daybreak-400 transition-all',
                item <= step ? 'w-full' : 'w-0',
              )}
            />
          </div>
        ))}
      </div>
      <div
        className="py-[16px]"
        style={{paddingLeft: scaleWidth(40), paddingRight: scaleWidth(40)}}
      >
        <div className="flex items-center justify-between">
          <button onClick={onPrevious}>
            <ArrowLeftIcon width={18} height={18} />
          </button>
          <div className="text-[13px] text-acai-900">
            {step + 1} - Select {stepTitles[step]}
          </div>
          <button onClick={onClose}>
            <CloseMenuIcon width={18} height={18} />
          </button>
        </div>
        {step === STEP_SERVICE && (
          <div className="mt-[16px]">
            <PostalSearchForm
              showEmptyFieldError={step === STEP_SERVICE}
              placeholder="Enter 6-digit postal code to view timeslots"
              hasResults={
                !isLoadingTimeslots && timeslots && timeslots?.length > 0
              }
              postalCodeFetchError={
                timeslotsError
                  ? 'There was an error getting timeslots. Please try again later.'
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
